<script setup>
/* global axios */
import { computed, ref, onMounted, onUnmounted, watch } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { createPendingMessage } from 'dashboard/helper/commons';
import { emitter } from 'shared/helpers/mitt';

const props = defineProps({
  conversationId: {
    type: [Number, String],
    required: true,
  },
});

const { t } = useI18n();
const store = useStore();

// useAlert is a function that takes message and optional action
const showAlert = (message, type = 'info') => {
  useAlert(message);
};

const currentUser = computed(() => store.getters.getCurrentUser);
const accountId = computed(() => store.getters.getCurrentAccountId);

const getIceServers = () => {
  const raw = window.chatwootConfig?.webrtcIceServers || '';
  if (Array.isArray(raw)) return raw;
  const v = String(raw || '').trim();
  if (v) {
    try {
      const parsed = JSON.parse(v);
      if (Array.isArray(parsed)) {
        console.log('[Dashboard WebRTC] Using ICE servers:', parsed);
        return parsed;
      }
    } catch (e) {
      console.error('[Dashboard WebRTC] Failed to parse ICE servers:', e);
    }
  }
  console.warn('[Dashboard WebRTC] Using fallback STUN only');
  return [{ urls: 'stun:stun.l.google.com:19302' }];
};

const conversation = computed(() => {
  const byId = store.getters.getConversationById?.(props.conversationId);
  return byId || store.getters.getSelectedChat;
});

// Keep request state in sync and auto-start P2P when the customer accepts.
// This avoids relying on parent header refs or selected-chat-only websocket updates.
const requestStatus = computed(() => {
  const attrs =
    conversation.value?.meta?.custom_attributes || conversation.value?.custom_attributes || {};
  return attrs.webcall_request_status || '';
});
const requestMode = computed(() => {
  const attrs =
    conversation.value?.meta?.custom_attributes || conversation.value?.custom_attributes || {};
  return attrs.webcall_mode || '';
});
const requestCallId = computed(() => {
  const attrs =
    conversation.value?.meta?.custom_attributes || conversation.value?.custom_attributes || {};
  return attrs.webcall_request_call_id || '';
});

watch(
  () => [requestStatus.value, requestMode.value, requestCallId.value],
  ([status, mode, rid]) => {
    // Only autostart for WebRTC accepted state
    if (status !== 'accepted' || mode !== 'webrtc' || !rid) return;
    if (p2pPeerConnection.value || isCallActive.value) return;
    // Best-effort: if browser blocks getUserMedia without gesture, startP2PCall will fail and UI will stay usable.
    startP2PCall({ callId: rid });
  },
  { immediate: true }
);

const isCallActive = ref(false);
const callId = ref(null);
const callDuration = ref(0);
const callTimer = ref(null);
const callStartTime = ref(null);
const peerConnection = ref(null);
const localStream = ref(null);
const remoteStream = ref(null);
const remoteAudioElement = ref(null);
const callStatus = ref('idle'); // 'idle', 'connecting', 'ringing', 'connected', 'ended'
const p2pPeerConnection = ref(null);
const p2pConnectTimeoutTimer = ref(null);
const p2pRemoteTrackTimeoutTimer = ref(null);
const pendingP2PIce = ref([]); // ICE candidates received before remoteDescription
const sentIceCandidates = ref(new Set()); // dedupe ICE candidate sends

const resetCallState = (nextStatus = 'idle') => {
  stopCallTimer();
  stopAudioLevelDetection();
  callDuration.value = 0;
  callStartTime.value = null;
  isCallActive.value = false;

  if (p2pConnectTimeoutTimer.value) {
    clearTimeout(p2pConnectTimeoutTimer.value);
    p2pConnectTimeoutTimer.value = null;
  }
  if (p2pRemoteTrackTimeoutTimer.value) {
    clearTimeout(p2pRemoteTrackTimeoutTimer.value);
    p2pRemoteTrackTimeoutTimer.value = null;
  }
  pendingP2PIce.value = [];
  sentIceCandidates.value = new Set();

  try {
    p2pPeerConnection.value?.close?.();
  } catch (e) {
    // ignore
  }
  p2pPeerConnection.value = null;

  if (localStream.value) {
    localStream.value.getTracks().forEach(track => track.stop());
    localStream.value = null;
  }
  if (remoteStream.value) {
    remoteStream.value.getTracks().forEach(track => track.stop());
    remoteStream.value = null;
  }
  if (remoteAudioElement.value) {
    remoteAudioElement.value.pause();
    remoteAudioElement.value.srcObject = null;
    remoteAudioElement.value.parentNode?.removeChild(remoteAudioElement.value);
    remoteAudioElement.value = null;
  }
  if (peerConnection.value) {
    try {
      peerConnection.value.close();
    } catch (e) {
      // ignore
    }
    peerConnection.value = null;
  }

  callId.value = null;
  callStatus.value = nextStatus;
};

const cleanupP2P = () => {
  try {
    p2pPeerConnection.value?.close?.();
  } catch (e) {
    // ignore
  }
  p2pPeerConnection.value = null;
};

const sendP2PSignal = async (call_id, signal_type, payload) => {
  await axios.post(`/api/v1/accounts/${accountId.value}/webcall/signal`, {
    conversation_id: props.conversationId,
    call_id,
    signal_type,
    payload,
  });
};

const sendIceCandidateOnce = (call_id, candidate) => {
  if (!candidate) return;
  const key = `${call_id}:${candidate.candidate || JSON.stringify(candidate)}`;
  if (sentIceCandidates.value.has(key)) return;
  sentIceCandidates.value.add(key);
  sendP2PSignal(call_id, 'ice', candidate);
};

// Pre-warm permissions in a user gesture (e.g. when agent clicks "request video call").
// This avoids browsers blocking getUserMedia when we auto-start after customer accepts.
const prewarmWebrtcMedia = async () => {
  try {
    if (localStream.value) return true;
    localStream.value = await navigator.mediaDevices.getUserMedia({ audio: true, video: true });
    applyMuteStates();
    return true;
  } catch (e) {
    showAlert('Kamera/Mikrofon izni alınamadı', 'error');
    return false;
  }
};

const startP2PCall = async ({ callId: requestCallId } = {}) => {
  const cid = requestCallId || callId.value;
  if (!cid) return false;
  if (p2pPeerConnection.value) return true;

  try {
    callStatus.value = 'connecting';
    isCallActive.value = true;
    callId.value = cid;

    // Fail fast if we don't reach connected state soon (prevents "connecting forever").
    if (p2pConnectTimeoutTimer.value) clearTimeout(p2pConnectTimeoutTimer.value);
    p2pConnectTimeoutTimer.value = setTimeout(async () => {
      // Still not connected -> fail
      if (callStatus.value === 'connecting') {
        try {
          await sendP2PSignal(cid, 'hangup', { reason: 'connection_timeout' });
        } catch (e) {
          // ignore
        }
        // Reuse existing failure cleanup
        // eslint-disable-next-line no-use-before-define
        await handleP2PFailure();
      }
    }, 25000);

    // Get media (camera+mic). Prefer already prewarmed stream (user gesture).
    if (!localStream.value) {
      localStream.value = await navigator.mediaDevices.getUserMedia({ audio: true, video: true });
    }
    applyMuteStates();

    const pc = new RTCPeerConnection({
      iceServers: getIceServers(),
    });
    p2pPeerConnection.value = pc;

    localStream.value.getTracks().forEach(track => pc.addTrack(track, localStream.value));

    pc.addEventListener('track', event => {
      if (!event.streams?.length) return;
      remoteStream.value = event.streams[0];
      if (!remoteAudioElement.value) {
        remoteAudioElement.value = document.createElement('audio');
        remoteAudioElement.value.autoplay = true;
        remoteAudioElement.value.setAttribute('playsinline', 'true');
        remoteAudioElement.value.volume = 1;
        document.body.appendChild(remoteAudioElement.value);
      }
      remoteAudioElement.value.srcObject = remoteStream.value;
      applyMuteStates();
      ensureRemotePlayback();
      setupAudioLevelDetection();
    });

    pc.addEventListener('icecandidate', evt => {
      if (evt.candidate) {
        sendIceCandidateOnce(cid, evt.candidate.toJSON ? evt.candidate.toJSON() : evt.candidate);
      }
    });

    // Only mark as connected once WebRTC transport is connected (prevents "connected but no media").
    const handleP2PFailure = async () => {
      if (callStatus.value === 'idle' || callStatus.value === 'ended') return;
      showAlert('WebRTC bağlantısı kurulamadı', 'error');
      resetCallState('idle');
      try {
        await axios.post(`/api/v1/accounts/${accountId.value}/webcall/reset`, {
          conversation_id: props.conversationId,
        });
      } catch (err) {
        // ignore
      }
      try {
        await sendP2PSignal(cid, 'hangup', { reason: 'webrtc_connection_failed' });
      } catch (err) {
        // ignore
      }
    };

    pc.addEventListener('connectionstatechange', () => {
      if (pc.connectionState === 'connected') {
        callStatus.value = 'connected';
        if (p2pConnectTimeoutTimer.value) {
          clearTimeout(p2pConnectTimeoutTimer.value);
          p2pConnectTimeoutTimer.value = null;
        }
        if (!isCallActive.value) {
          isCallActive.value = true;
          startCallTimer();
        }
        setupAudioLevelDetection();

        // If we become connected but never receive a remote track, treat as failure.
        if (p2pRemoteTrackTimeoutTimer.value) clearTimeout(p2pRemoteTrackTimeoutTimer.value);
        p2pRemoteTrackTimeoutTimer.value = setTimeout(() => {
          const remoteHasTracks =
            !!remoteStream.value?.getTracks?.()?.length ||
            !!remoteStream.value?.getAudioTracks?.()?.length ||
            !!remoteStream.value?.getVideoTracks?.()?.length;
          if (!remoteHasTracks) {
            handleP2PFailure();
          }
        }, 8000);
      }
      if (pc.connectionState === 'failed' || pc.connectionState === 'disconnected') {
        handleP2PFailure();
      }
    });

    pc.addEventListener('iceconnectionstatechange', () => {
      if (pc.iceConnectionState === 'failed') {
        handleP2PFailure();
      }
    });

    // IMPORTANT: Agent is the ANSWERER. Customer(widget) creates the offer (user gesture).
    return true;
  } catch (e) {
    showAlert(t('CONVERSATION.HEADER.WEBCALL.CALL_START_FAILED'), 'error');
    resetCallState('idle');
    // Ensure both sides exit cleanly; otherwise customer waits until timeout and agent banner may appear stuck.
    try {
      await axios.post(`/api/v1/accounts/${accountId.value}/webcall/reset`, {
        conversation_id: props.conversationId,
      });
    } catch (err) {
      // ignore
    }
    try {
      await sendP2PSignal(cid, 'hangup', { reason: 'agent_media_failed' });
    } catch (err) {
      // ignore
    }
    return false;
  }
};

const microphoneLevel = ref(0);
const speakerLevel = ref(0);
const audioContext = ref(null);
const microphoneAnalyser = ref(null);
const speakerAnalyser = ref(null);
const animationFrameId = ref(null);

const isMicrophoneMuted = ref(false);
const isSpeakerMuted = ref(false);
const isCameraMuted = ref(false);

const applyMuteStates = () => {
  if (localStream.value) {
    localStream.value.getAudioTracks().forEach(track => {
      track.enabled = !isMicrophoneMuted.value;
    });
    localStream.value.getVideoTracks().forEach(track => {
      track.enabled = !isCameraMuted.value;
    });
  }
  if (remoteAudioElement.value) {
    remoteAudioElement.value.muted = isSpeakerMuted.value;
  }
};

const toggleMicrophoneMute = () => {
  isMicrophoneMuted.value = !isMicrophoneMuted.value;
  applyMuteStates();
};

const toggleSpeakerMute = () => {
  isSpeakerMuted.value = !isSpeakerMuted.value;
  applyMuteStates();
};

const toggleCameraMute = () => {
  isCameraMuted.value = !isCameraMuted.value;
  applyMuteStates();
};

function ensureRemotePlayback() {
  // Autoplay can be blocked in some browsers. Retry on next user gesture.
  if (!remoteAudioElement.value) return;

  const tryPlay = () => {
    if (!remoteAudioElement.value) return;
    if (audioContext.value?.state === 'suspended') {
      audioContext.value.resume?.().catch?.(() => {});
    }
    remoteAudioElement.value.play?.().catch?.(() => {});
  };

  tryPlay();

  const onUserGesture = () => {
    tryPlay();
    window.removeEventListener('click', onUserGesture, true);
    window.removeEventListener('keydown', onUserGesture, true);
    window.removeEventListener('touchstart', onUserGesture, true);
  };

  window.addEventListener('click', onUserGesture, true);
  window.addEventListener('keydown', onUserGesture, true);
  window.addEventListener('touchstart', onUserGesture, true);
}

const stopAudioLevelDetection = () => {
  if (animationFrameId.value) {
    cancelAnimationFrame(animationFrameId.value);
    animationFrameId.value = null;
  }
  microphoneLevel.value = 0;
  speakerLevel.value = 0;
  microphoneAnalyser.value = null;
  speakerAnalyser.value = null;
  if (audioContext.value) {
    audioContext.value.close?.().catch?.(() => {});
    audioContext.value = null;
  }
};

const setupAudioLevelDetection = () => {
  stopAudioLevelDetection();

  const AudioContext = window.AudioContext || window.webkitAudioContext;
  if (!AudioContext) return;

  try {
    audioContext.value = new AudioContext();
  } catch (e) {
    return;
  }

  if (localStream.value) {
    try {
      const source = audioContext.value.createMediaStreamSource(localStream.value);
      microphoneAnalyser.value = audioContext.value.createAnalyser();
      microphoneAnalyser.value.fftSize = 256;
      source.connect(microphoneAnalyser.value);
    } catch (e) {
      microphoneAnalyser.value = null;
    }
  }

  // Prefer remoteStream for analysis; works even if autoplay is blocked.
  if (remoteStream.value) {
    try {
      const source = audioContext.value.createMediaStreamSource(remoteStream.value);
      speakerAnalyser.value = audioContext.value.createAnalyser();
      speakerAnalyser.value.fftSize = 256;
      source.connect(speakerAnalyser.value);
    } catch (e) {
      speakerAnalyser.value = null;
    }
  }

  const calcLevel = analyser => {
    if (!analyser) return 0;
    const dataArray = new Uint8Array(analyser.frequencyBinCount);
    analyser.getByteFrequencyData(dataArray);
    let sum = 0;
    for (let i = 0; i < dataArray.length; i += 1) sum += dataArray[i];
    const avg = sum / dataArray.length; // 0..255
    return Math.min(100, Math.max(0, Math.round((avg / 255) * 100)));
  };

  const tick = () => {
    microphoneLevel.value = calcLevel(microphoneAnalyser.value);
    speakerLevel.value = calcLevel(speakerAnalyser.value);
    animationFrameId.value = requestAnimationFrame(tick);
  };

  tick();
};

// Get current contact for phone number check
const currentContact = computed(() => {
  const chat = conversation.value;
  if (!chat?.meta?.sender?.id) return null;
  return store.getters['contacts/getContact'](chat.meta.sender.id);
});

// Check if agent has santral ID configured and contact has phone number
const canMakeCall = computed(() => {
  const config = window.chatwootConfig || {};
  const chat = conversation.value;
  const phoneNumber =
    currentContact.value?.phone_number || chat?.meta?.sender?.phone_number;
  return !!(
    currentUser.value?.n_santral_id &&
    phoneNumber &&
    config.webcallBaseUrl
  );
});

// Format duration as MM:SS
const formattedDuration = computed(() => {
  const minutes = Math.floor(callDuration.value / 60);
  const seconds = callDuration.value % 60;
  return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
});

// Start call timer
const startCallTimer = () => {
  callStartTime.value = Date.now();
  callTimer.value = setInterval(() => {
    if (callStartTime.value) {
      callDuration.value = Math.floor((Date.now() - callStartTime.value) / 1000);
    }
  }, 1000);
};

// Stop call timer
const stopCallTimer = () => {
  if (callTimer.value) {
    clearInterval(callTimer.value);
    callTimer.value = null;
  }
  callStartTime.value = null;
  callDuration.value = 0;
};

// Start WebRTC call
const startCall = async ({ withVideo = false } = {}) => {
  if (!canMakeCall.value) {
    showAlert('Temsilci santral ID\'si yapılandırılmamış', 'error');
    return;
  }

  try {
    // Set initial call status
    callStatus.value = 'connecting';
    
    // Request media permission and get stream with high quality settings
    const mediaConstraints = {
      audio: {
        echoCancellation: true,
        noiseSuppression: true,
        autoGainControl: true,
        sampleRate: 48000, // High quality sample rate
        channelCount: 1, // Mono for voice
        // Prefer Opus codec for better quality
        googEchoCancellation: true,
        googNoiseSuppression: true,
        googAutoGainControl: true,
        googHighpassFilter: true,
        googTypingNoiseDetection: true,
        googNoiseReduction: true,
      },
      video: withVideo
    };
    localStream.value = await navigator.mediaDevices.getUserMedia(mediaConstraints);
    applyMuteStates();
    
    // Apply audio constraints to tracks for better quality
    localStream.value.getAudioTracks().forEach(track => {
      const settings = track.getSettings();
      // Audio track settings logged
    });

    // Create RTCPeerConnection
    const pc = new RTCPeerConnection({
      iceServers: getIceServers(),
    });
    peerConnection.value = pc;

    // Remote audio (speaker output)
    pc.addEventListener('track', event => {
      if (!event.streams?.length) return;
      remoteStream.value = event.streams[0];

      if (!remoteAudioElement.value) {
        remoteAudioElement.value = document.createElement('audio');
        remoteAudioElement.value.autoplay = true;
        remoteAudioElement.value.setAttribute('playsinline', 'true');
        remoteAudioElement.value.volume = 1;
        document.body.appendChild(remoteAudioElement.value);
      }

      remoteAudioElement.value.srcObject = remoteStream.value;
      applyMuteStates();
      ensureRemotePlayback();
      setupAudioLevelDetection();
    });

    // Add local stream tracks to peer connection with codec preferences and bitrate settings
    localStream.value.getTracks().forEach(track => {
      const sender = pc.addTrack(track, localStream.value);
      
      // Set codec preferences for better audio quality (Opus preferred)
      if (sender && 'setCodecPreferences' in sender) {
        try {
          const codecs = RTCRtpReceiver.getCapabilities('audio').codecs;
          // Prefer Opus codec (best quality for voice)
          const opusCodec = codecs.find(codec => codec.mimeType === 'audio/opus');
          const preferredCodecs = opusCodec 
            ? [opusCodec, ...codecs.filter(c => c !== opusCodec)]
            : codecs;
          sender.setCodecPreferences(preferredCodecs);
        } catch (e) {
          // Could not set codec preferences
        }
      }
      
      // Set bitrate and quality parameters for better audio quality
      if (sender && 'getParameters' in sender) {
        try {
          const params = sender.getParameters();
          if (!params.encodings || params.encodings.length === 0) {
            params.encodings = [{}];
          }
          
          // Set high bitrate for better quality (64-128 kbps for Opus)
          params.encodings.forEach(encoding => {
            encoding.maxBitrate = 128000; // 128 kbps for high quality voice
            encoding.maxptime = 60; // Maximum packetization time (ms)
            encoding.ptime = 20; // Preferred packetization time (ms)
          });
          
          sender.setParameters(params);
        } catch (e) {
          // Could not set parameters
        }
      }
    });
    
    // Note: Receiver parameters cannot be set directly in WebRTC
    // Bitrate is controlled by the sender and SDP negotiation

    // Create offer with high quality audio settings
    const offer = await pc.createOffer({
      offerToReceiveAudio: true,
      offerToReceiveVideo: withVideo
    });
    
    // Optimize SDP for better audio quality
    if (offer.sdp) {
      // Prefer Opus codec and set high bitrate
      offer.sdp = offer.sdp.replace(/a=rtpmap:(\d+) opus\/48000\/2/g, (match, payload) => {
        return `${match}\r\na=fmtp:${payload} minptime=10;useinbandfec=1;stereo=0;sprop-stereo=0;maxaveragebitrate=128000`;
      });
      
      // Ensure Opus is preferred
      const lines = offer.sdp.split('\r\n');
      const mLineIndex = lines.findIndex(line => line.startsWith('m=audio'));
      if (mLineIndex !== -1) {
        const mLine = lines[mLineIndex];
        const opusPayload = offer.sdp.match(/a=rtpmap:(\d+) opus/);
        if (opusPayload) {
          const opusPayloadNum = opusPayload[1];
          const payloads = mLine.split(' ').slice(3);
          const opusIndex = payloads.indexOf(opusPayloadNum);
          if (opusIndex > 0) {
            // Move Opus to the front
            payloads.splice(opusIndex, 1);
            payloads.unshift(opusPayloadNum);
            lines[mLineIndex] = mLine.split(' ').slice(0, 3).join(' ') + ' ' + payloads.join(' ');
            offer.sdp = lines.join('\r\n');
          }
        }
      }
    }
    
    await pc.setLocalDescription(offer);
    callStatus.value = 'ringing';

    // Wait for ICE gathering to complete (or timeout after 5 seconds)
    const offerWithIce = await new Promise((resolve) => {
      if (pc.iceGatheringState === 'complete') {
        resolve(pc.localDescription);
      } else {
        const checkState = () => {
          if (pc.iceGatheringState === 'complete') {
            resolve(pc.localDescription);
          }
        };
        pc.addEventListener('icegatheringstatechange', checkState);
        setTimeout(() => resolve(pc.localDescription), 5000); // Timeout after 5 seconds
      }
    });

    // Send offerSdp to backend (use axios so auth headers/cookies are handled consistently)
    const { data } = await axios.post(
      `/api/v1/accounts/${accountId.value}/webcall/start`,
      {
        conversation_id: props.conversationId,
        offerSdp: offerWithIce.sdp,
      }
    );

    if (data.success) {
      callId.value = data.call_id || data.callId;

      // Set remote description (answerSdp from backend bridge)
      // This is safe because it comes from our backend bridge, not directly from external service
      if (data.answerSdp || data.answer_sdp) {
        try {
          await pc.setRemoteDescription(
            new RTCSessionDescription({
              type: 'answer',
              sdp: data.answerSdp || data.answer_sdp,
            })
          );
        } catch (error) {
          // Error setting remote description
          endCall();
          return;
        }
      }

      // Monitor connection state
      pc.addEventListener('connectionstatechange', () => {
        if (pc.connectionState === 'connected') {
          callStatus.value = 'connected';
          isCallActive.value = true;
          startCallTimer();
          sendSystemMessage(`${currentUser.value.name} sesli konuşma başlattı`);
        } else if (pc.connectionState === 'connecting') {
          callStatus.value = 'connecting';
        } else if (pc.connectionState === 'failed' || pc.connectionState === 'disconnected') {
          callStatus.value = 'ended';
          endCall();
        }
      });

      // Also check ice connection state
      pc.addEventListener('iceconnectionstatechange', () => {
        if (pc.iceConnectionState === 'connected' || pc.iceConnectionState === 'completed') {
          if (callStatus.value === 'ringing' || callStatus.value === 'connecting') {
            callStatus.value = 'connected';
            isCallActive.value = true;
            startCallTimer();
            sendSystemMessage(`${currentUser.value.name} sesli konuşma başlattı`);
          }
        } else if (pc.iceConnectionState === 'checking') {
          callStatus.value = 'connecting';
        } else if (pc.iceConnectionState === 'failed' || pc.iceConnectionState === 'disconnected') {
          callStatus.value = 'ended';
          endCall();
        }
      });

      // Send system message about call start
      await sendSystemMessage(
        `${currentUser.value.name} sesli konuşma başlattı`
      );

      showAlert('Arama başlatıldı', 'success');
    } else {
      // Cleanup on failure
      if (localStream.value) {
        localStream.value.getTracks().forEach(track => track.stop());
        localStream.value = null;
      }
      if (pc) {
        pc.close();
        peerConnection.value = null;
      }
      showAlert(data.error || 'Arama başlatılamadı', 'error');
      showSupportMessage();
    }
  } catch (error) {
    // WebCall start error
    
    // Cleanup on error
    if (localStream.value) {
      localStream.value.getTracks().forEach(track => track.stop());
      localStream.value = null;
    }
    if (peerConnection.value) {
      peerConnection.value.close();
      peerConnection.value = null;
    }
    
    if (error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError') {
      showAlert('Mikrofon izni verilmedi', 'error');
    } else {
      showAlert('Arama başlatılamadı', 'error');
      showSupportMessage();
    }
  }
};

// End WebRTC call
const endCall = async () => {
  // Allow ending even if not fully connected yet; always cleanup UI state.
  if (!callId.value) {
    resetCallState('idle');
    return;
  }

  try {
    const response = await axios.post(`/api/v1/accounts/${accountId.value}/webcall/hangup`, {
      conversation_id: props.conversationId,
      call_id: callId.value,
      callId: callId.value, // Also send camelCase for consistency
    });

    const data = response.data;

    // Cleanup WebRTC resources regardless of response
    resetCallState('ended');

    if (data.success) {
      const duration = formattedDuration.value;
      // keep 'ended' briefly, then go idle
      setTimeout(() => {
        if (callStatus.value === 'ended') callStatus.value = 'idle';
      }, 1500);

      // Send system message about call end
      await sendSystemMessage(
        `Sesli sohbet kapatıldı (Süre: ${duration})`
      );

      showAlert('Arama sonlandırıldı', 'success');
    } else {
      showAlert(data.error || 'Arama sonlandırılamadı', 'error');
    }
  } catch (error) {
    // WebCall hangup error
    
    // Cleanup WebRTC resources on error
    resetCallState('idle');
    
    showAlert('Arama sonlandırılamadı', 'error');
  }
};

// Send system message
const sendSystemMessage = async (content) => {
  try {
    const pendingMessage = createPendingMessage({
      content,
      messageType: 'outgoing',
      private: false,
      conversationId: props.conversationId,
    });
    await store.dispatch('conversations/sendMessageWithData', pendingMessage);
  } catch (error) {
    // Failed to send system message
  }
};

// Show support message if call fails
const showSupportMessage = () => {
  const rawWhatsapp =
    window.chatwootConfig?.siteSupportWhatsapp ||
    window.chatwootConfig?.webcallSupportWhatsapp ||
    '';
  const rawPhone =
    window.chatwootConfig?.siteSupportPhone ||
    window.chatwootConfig?.webcallSupportPhone ||
    '';

  const toList = value => {
    if (value == null) return [];
    if (Array.isArray(value)) return value;
    if (typeof value === 'object') return [value];
    const v = String(value).trim();
    if (!v || v === '0' || v === '1') return [];
    if (v.startsWith('[') || v.startsWith('{')) {
      try {
        const parsed = JSON.parse(v);
        if (Array.isArray(parsed)) return parsed;
        if (parsed && typeof parsed === 'object') return [parsed];
      } catch (e) {
        // fall through
      }
    }
    return v.split(',').map(s => s.trim()).filter(Boolean);
  };

  const normalizeItem = item => {
    if (item == null) return '';
    if (typeof item === 'string' || typeof item === 'number') {
      const v = String(item).trim();
      if (!v || v === '0' || v === '1') return '';
      return v;
    }
    if (typeof item === 'object') {
      const active = item.active;
      if (active === false || active === 'false' || active === 0 || active === '0') return '';
      const candidate =
        item.value ??
        item.phone ??
        item.number ??
        item.tel ??
        item.whatsapp ??
        '';
      const v = String(candidate || '').trim();
      if (!v || v === '0' || v === '1') return '';
      return v;
    }
    return '';
  };

  const whatsappList = toList(rawWhatsapp).map(normalizeItem).filter(Boolean);
  const phoneList = toList(rawPhone).map(normalizeItem).filter(Boolean);

  if (!whatsappList.length && !phoneList.length) return;

  const messageTr = 'Konuşma esnasında sorun yaşamanız durumunda bizlerle iletişime geçebilirsiniz:';
  const messageEn = 'If you experience any issues during the call, you can contact us:';
  
  const message = `
${messageTr}

${messageEn}

${whatsappList.map(v => `WhatsApp: ${v}`).join('\n')}
${phoneList.map(v => `Phone: ${v}`).join('\n')}
  `.trim();

  sendSystemMessage(message);
};

onUnmounted(() => {
  stopCallTimer();
  stopAudioLevelDetection();
  // End call if component is unmounted while call is active
  if (isCallActive.value && callId.value) {
    endCall();
  }
  
  // Cleanup WebRTC resources
  if (localStream.value) {
    localStream.value.getTracks().forEach(track => track.stop());
    localStream.value = null;
  }
  if (remoteStream.value) {
    remoteStream.value.getTracks().forEach(track => track.stop());
    remoteStream.value = null;
  }
  if (remoteAudioElement.value) {
    remoteAudioElement.value.pause();
    remoteAudioElement.value.srcObject = null;
    remoteAudioElement.value.parentNode?.removeChild(remoteAudioElement.value);
    remoteAudioElement.value = null;
  }
  if (peerConnection.value) {
    peerConnection.value.close();
    peerConnection.value = null;
  }

  cleanupP2P();
});

// Expose call status and methods for parent component
defineExpose({
  callStatus,
  isCallActive,
  callId,
  formattedDuration,
  microphoneLevel,
  speakerLevel,
  isMicrophoneMuted,
  isSpeakerMuted,
  isCameraMuted,
  toggleMicrophoneMute,
  toggleSpeakerMute,
  toggleCameraMute,
  prewarmWebrtcMedia,
  localStream,
  remoteStream,
  startCall,
  startP2PCall,
  endCall,
});

onMounted(() => {
  emitter.on('WEBCALL_P2P_SIGNAL', async (wrapper) => {
    console.log('[Dashboard WebRTC] WEBCALL_P2P_SIGNAL received:', wrapper);
    // Parse wrapper to get actual data (matches widget format)
    const data = wrapper?.data;
    if (!data) {
      console.warn('[Dashboard WebRTC] No data in wrapper');
      return;
    }
    
    console.log('[Dashboard WebRTC] P2P signal data:', data);
    
    // Filter: only for this conversation (and optionally this agent)
    if (!data?.conversation_id) {
      console.warn('[Dashboard WebRTC] No conversation_id in data');
      return;
    }
    if (data.conversation_id.toString() !== props.conversationId?.toString()) {
      console.log('[Dashboard WebRTC] Wrong conversation:', data.conversation_id, 'expected:', props.conversationId);
      return;
    }
    if (data.agent_id && currentUser.value?.id && data.agent_id !== currentUser.value.id) {
      console.log('[Dashboard WebRTC] Wrong agent:', data.agent_id, 'expected:', currentUser.value?.id);
      return;
    }

    const cid = data.call_id || callId.value;
    const type = data.signal_type;
    const payload = data.payload;
    if (!cid) {
      console.warn('[Dashboard WebRTC] No call_id');
      return;
    }

    console.log('[Dashboard WebRTC] Processing signal type:', type, 'for call:', cid);

    if (type === 'offer') {
      console.log('[Dashboard WebRTC] Received offer, creating answer...');
      // Ensure we have a peer connection ready (local tracks already prewarmed on request click).
      if (!p2pPeerConnection.value) {
        const ok = await startP2PCall({ callId: cid });
        if (ok === false) return;
      }
      try {
        await p2pPeerConnection.value.setRemoteDescription(new RTCSessionDescription(payload));
        // Apply any queued ICE candidates that arrived before remoteDescription.
        const queued = pendingP2PIce.value || [];
        pendingP2PIce.value = [];
        for (const c of queued) {
          try {
            // eslint-disable-next-line no-await-in-loop
            await p2pPeerConnection.value.addIceCandidate(new RTCIceCandidate(c));
          } catch (e) {
            // ignore
          }
        }
        const answer = await p2pPeerConnection.value.createAnswer();
        await p2pPeerConnection.value.setLocalDescription(answer);
        await sendP2PSignal(cid, 'answer', { sdp: answer.sdp, type: answer.type });
        callStatus.value = 'connecting';
      } catch (e) {
        // If anything fails, close down cleanly
        resetCallState('idle');
      }
      return;
    }

    if (type === 'answer') {
      if (!p2pPeerConnection.value) return;
      await p2pPeerConnection.value.setRemoteDescription(new RTCSessionDescription(payload));
      // Wait for connectionState === 'connected' before marking as connected/ticking timer.
      callStatus.value = 'connecting';
    }
    if (type === 'ice') {
      if (!p2pPeerConnection.value) return;
      try {
        // If remoteDescription isn't set yet (offer just received), queue and apply later.
        if (!p2pPeerConnection.value.remoteDescription) {
          pendingP2PIce.value = [...(pendingP2PIce.value || []), payload];
          return;
        }
        await p2pPeerConnection.value.addIceCandidate(new RTCIceCandidate(payload));
      } catch (e) {
        // ignore
      }
    }
    if (type === 'hangup') {
      resetCallState('ended');
      setTimeout(() => {
        if (callStatus.value === 'ended') callStatus.value = 'idle';
      }, 1500);
    }
  });
});

watch(localStream, () => applyMuteStates());
watch(remoteAudioElement, () => applyMuteStates());
</script>

<template>
  <!-- Always render for ref access to get call status, even when canMakeCall is false -->
  <!-- Widget tarafından başlatılan aramaları görmek için her zaman render edilmeli -->
  <div class="flex items-center gap-2" :class="{ 'hidden': !canMakeCall }">
    <button
      v-if="canMakeCall && !isCallActive && callStatus === 'idle'"
      v-tooltip="t('CONVERSATION.HEADER.WEBCALL.CALL_CUSTOMER')"
      class="flex items-center justify-center w-8 h-8 rounded-full hover:bg-n-weak transition-colors"
      @click="startCall"
    >
      <fluent-icon icon="call" size="14" class="text-n-slate-12" />
    </button>
    <div
      v-if="canMakeCall && (isCallActive || (callStatus !== 'idle' && callStatus !== 'ended'))"
      class="flex items-center gap-2 px-2 py-1 rounded bg-n-green-10 text-white"
    >
      <button
        v-tooltip="t('CONVERSATION.HEADER.WEBCALL.END_CALL')"
        class="flex items-center justify-center w-6 h-6 rounded-full hover:bg-n-green-9 transition-colors"
        @click="endCall"
      >
        <fluent-icon icon="call-end" size="14" />
      </button>
      <span class="text-xs font-mono">{{ formattedDuration }}</span>
    </div>
  </div>
</template>

